// Helpers puros da Home (sem JSX, sem efeitos colaterais).

import { ETAPA, ETAPA_LABELS } from "../../utils/toc/etapas";

export const ROTA_POR_ETAPA = {
  [ETAPA.COMPRAS]: "/compras",
  [ETAPA.MAQUINAS]: "/industrializacao",
  [ETAPA.TERCEIROS]: "/terceiros",
  [ETAPA.MARCENARIA]: "/marcenaria",
  [ETAPA.ACABAMENTO]: "/acabamento",
  [ETAPA.EXPEDICAO]: "/entregas",
};

export function rotaDaEtapa(etapa) {
  return ROTA_POR_ETAPA[etapa] || null;
}

export function formatGargaloLabel(etapa) {
  return ETAPA_LABELS[etapa] || etapa || "—";
}

/**
 * Mapeia os 14 IDs de painéis em 3 grupos seguindo o fluxo TOC.
 * IDs reconhecidos: liberacao, acabamentos (definição), medicao,
 * executivo, aprovacao, ppcp, compras, ferragens, industrializacao,
 * terceiros, marcenaria, acabamento (etapa), entregas, obras.
 */
const GRUPO_POR_ID = {
  liberacao: "Pré-PPCP",
  acabamentos: "Pré-PPCP",
  medicao: "Pré-PPCP",
  executivo: "Pré-PPCP",
  aprovacao: "Pré-PPCP",
  ppcp: "Pré-PPCP",
  compras: "Fluxo TOC",
  ferragens: "Fluxo TOC",
  industrializacao: "Fluxo TOC",
  terceiros: "Fluxo TOC",
  marcenaria: "Fluxo TOC",
  acabamento: "Fluxo TOC",
  entregas: "Saída",
  obras: "Saída",
};

const ORDEM_GRUPOS = ["Pré-PPCP", "Fluxo TOC", "Saída"];

const ETAPA_POR_PAINEL = {
  compras: ETAPA.COMPRAS,
  industrializacao: ETAPA.MAQUINAS,
  terceiros: ETAPA.TERCEIROS,
  marcenaria: ETAPA.MARCENARIA,
  acabamento: ETAPA.ACABAMENTO,
  entregas: ETAPA.EXPEDICAO,
};

export function isPainelDaEtapa(painelId, etapa) {
  return ETAPA_POR_PAINEL[painelId] === etapa;
}

export function groupAtalhos(paineis) {
  const grupos = ORDEM_GRUPOS.map((titulo) => ({ titulo, paineis: [] }));
  const indexByTitulo = new Map(grupos.map((g, i) => [g.titulo, i]));
  const outros = { titulo: "Outros", paineis: [] };

  paineis.forEach((painel) => {
    const tituloGrupo = GRUPO_POR_ID[painel.id];
    if (tituloGrupo && indexByTitulo.has(tituloGrupo)) {
      grupos[indexByTitulo.get(tituloGrupo)].paineis.push(painel);
    } else {
      outros.paineis.push(painel);
    }
  });

  const resultado = grupos.filter((g) => g.paineis.length > 0);
  if (outros.paineis.length > 0) resultado.push(outros);
  return resultado;
}

/**
 * Próxima ação recomendada para a Home — regra hierárquica.
 * Recebe:
 *  - toc: resultado de useTOC
 *  - calibracaoCompleta: boolean
 *  - atrasadosGlobal: número (entregas vencidas hoje na visão "ativos")
 */
export function buildNextAction(toc, { calibracaoCompleta, atrasadosGlobal }) {
  const zonas = toc?.contagemPorZona || {};
  if ((zonas.preto || 0) > 0) {
    return {
      tone: "danger",
      title: `${zonas.preto} ambiente${zonas.preto === 1 ? "" : "s"} em zona preta`,
      body: "Buffer estourado — prazo de contrato comprometido.",
      ctaLabel: "Ver críticos",
      cta: { kind: "scroll-criticos" },
    };
  }
  if ((zonas.vermelho || 0) > 0) {
    return {
      tone: "danger",
      title: `${zonas.vermelho} em zona vermelha`,
      body: "Risco alto — ≥67% do pulmão consumido.",
      ctaLabel: "Ver críticos",
      cta: { kind: "scroll-criticos" },
    };
  }
  if (atrasadosGlobal > 0) {
    return {
      tone: "warning",
      title: `${atrasadosGlobal} entrega${atrasadosGlobal === 1 ? "" : "s"} vencida${atrasadosGlobal === 1 ? "" : "s"}`,
      body: "Verificar reprogramações com clientes.",
      ctaLabel: "Abrir entregas",
      cta: { kind: "navigate", to: "/entregas" },
    };
  }
  if (
    calibracaoCompleta &&
    toc?.gargalo &&
    (toc.gargalo.utilizacao ?? 0) >= 0.9
  ) {
    return {
      tone: "warning",
      title: `Gargalo saturado em ${ETAPA_LABELS[toc.gargalo.etapa] || toc.gargalo.etapa}`,
      body: `Utilização em ${Math.round((toc.gargalo.utilizacao || 0) * 100)}%.`,
      ctaLabel: "Abrir KPIs PPCP",
      cta: { kind: "navigate", to: "/kpis-ppcp" },
    };
  }
  if ((zonas.amarelo || 0) > 0) {
    return {
      tone: "info",
      title: `${zonas.amarelo} em zona amarela`,
      body: "Monitorar — risco crescente de consumo de buffer.",
      ctaLabel: "Ver críticos",
      cta: { kind: "scroll-criticos" },
    };
  }
  return {
    tone: "success",
    title: "Fluxo saudável",
    body: "Sem ações críticas no momento.",
    ctaLabel: null,
    cta: null,
  };
}

export function formatPct(value) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

export function formatSaudacao(date = new Date()) {
  const hora = date.getHours();
  if (hora < 12) return "Bom dia";
  if (hora < 18) return "Boa tarde";
  return "Boa noite";
}

export function formatDataExtenso(date = new Date()) {
  return date.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}
